import React from 'react'
import { Button, Col, Dropdown, Row, Table, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { MoreCircle } from 'iconsax-react'
import { sortFieldJobApplicantsEnum } from '../hooks/jobVacancyEnum'
import { rupiahFormat } from 'helpers/formatter'
import {
  AdminJobBoardsVacanciesGetListApplicantsResponseItems,
  AdminJobBoardsVacanciesGetListApplicantsResponseItemsWage,
  GenderEnum,
  JobBoardsVacanciesApplicationStatusEnum,
  OrderDirectionEnum,
} from 'generated/api'
import IconSortAscDesc from 'components/tableHelpers/IconSortAscDesc'
import { IconCaretDown } from 'components/icons'
import Badge from 'components/badge/Badge'
import { mapJobVacancyApplicantStatus } from 'helpers/wording'
import jobVacancyParticipantStatusBadgeVariant from '../hooks/jobVacancyParticipantStatusBadgeVariant'

interface IJobVacancyTableProps {
  data: AdminJobBoardsVacanciesGetListApplicantsResponseItems[]
  loading: boolean
  columnsSortBy: OrderDirectionEnum
  columnsSortField: sortFieldJobApplicantsEnum
  onSort: (field: sortFieldJobApplicantsEnum) => void
  onOpenModalUpdateApplicantStatus: (
    id: string,
    nextStatus: JobBoardsVacanciesApplicationStatusEnum,
  ) => void
}

export default function JobVacancyApplicantTable(props: IJobVacancyTableProps) {
  const {
    loading,
    data,
    columnsSortBy,
    columnsSortField,
    onSort,
    onOpenModalUpdateApplicantStatus,
  } = props

  const columns: ColumnsType<AdminJobBoardsVacanciesGetListApplicantsResponseItems> = [
    {
      width: '5%',
      dataIndex: 'id',
      key: 'id',
      title: (
        <div className="flex">
          <Typography.Text className="text-sm font-semibold text-[#333539]">No.</Typography.Text>
        </div>
      ),
      render: (_text, _row, index) => (
        <div className="flex flex-col">
          <Typography.Text className="text-sm font-normal text-[#333539]">
            {index + 1}.
          </Typography.Text>
        </div>
      ),
    },
    {
      width: '10%',
      dataIndex: 'name',
      key: 'name',
      title: (
        <div
          data-testid="table_head_name"
          className="flex justify-between items-center hover:cursor-pointer"
        >
          <Typography.Text className="text-sm font-semibold text-[#333539]">Nama</Typography.Text>
          <IconSortAscDesc
            columnName={sortFieldJobApplicantsEnum.NAME}
            columnSortBy={columnsSortBy}
            columnSortField={columnsSortField}
          />
        </div>
      ),
      render: name => (
        <div
          data-testid="table_body_name"
          className="flex flex-col text-sm font-normal text-[#333539]"
        >
          <Typography.Text>{name}</Typography.Text>
        </div>
      ),
      onHeaderCell: () => {
        return {
          onClick: () => onSort(sortFieldJobApplicantsEnum.NAME),
        }
      },
    },
    {
      width: '15%',
      dataIndex: 'gender',
      key: 'gender',
      title: (
        <div
          data-testid="table_head_gender"
          className="flex justify-between items-center hover:cursor-pointer"
        >
          <Typography.Text className="text-sm font-semibold text-[#333539]">
            Jenis Kelamin
          </Typography.Text>
          <IconSortAscDesc
            columnName={sortFieldJobApplicantsEnum.GENDER}
            columnSortBy={columnsSortBy}
            columnSortField={columnsSortField}
          />
        </div>
      ),
      render: gender => (
        <div
          data-testid="table_body_gender"
          className="flex flex-col text-sm font-normal text-[#333539]"
        >
          <Typography.Text>
            {gender === GenderEnum.Male ? 'Laki-laki' : 'Perempuan'}
          </Typography.Text>
        </div>
      ),
      onHeaderCell: () => {
        return {
          onClick: () => onSort(sortFieldJobApplicantsEnum.GENDER),
        }
      },
    },
    {
      width: '15%',
      dataIndex: 'phoneNo',
      key: 'phoneNo',
      title: (
        <div
          data-testid="table_head_phoneNo"
          className="flex justify-between items-center hover:cursor-pointer"
        >
          <Typography.Text className="text-sm font-semibold text-[#333539]">
            Nomor Handphone
          </Typography.Text>
          <IconSortAscDesc
            columnName={sortFieldJobApplicantsEnum.PHONENO}
            columnSortBy={columnsSortBy}
            columnSortField={columnsSortField}
          />
        </div>
      ),
      render: phoneNo => (
        <div
          data-testid="table_body_phoneNo"
          className="flex flex-col text-sm font-normal text-[#333539]"
        >
          <Typography.Text>{phoneNo}</Typography.Text>
        </div>
      ),
      onHeaderCell: () => {
        return {
          onClick: () => onSort(sortFieldJobApplicantsEnum.PHONENO),
        }
      },
    },
    {
      width: '15%',
      dataIndex: 'address',
      key: 'address',
      title: (
        <div
          data-testid="table_head_address"
          className="flex justify-between items-center hover:cursor-pointer"
        >
          <Typography.Text className="text-sm font-semibold text-[#333539]">
            Domisili
          </Typography.Text>
          <IconSortAscDesc
            columnName={sortFieldJobApplicantsEnum.ADDRESS}
            columnSortBy={columnsSortBy}
            columnSortField={columnsSortField}
          />
        </div>
      ),
      render: address => (
        <div
          data-testid="table_body_address"
          className="flex flex-col text-sm font-normal text-[#333539]"
        >
          <Typography.Text>{address}</Typography.Text>
        </div>
      ),
      onHeaderCell: () => {
        return {
          onClick: () => onSort(sortFieldJobApplicantsEnum.ADDRESS),
        }
      },
    },
    {
      width: '10%',
      dataIndex: 'jobExperience',
      key: 'jobExperience',
      title: (
        <div
          data-testid="table_head_jobExperience"
          className="flex justify-between items-center hover:cursor-pointer gap-1"
        >
          <Typography.Text className="text-sm font-semibold text-[#333539] break-normal">
            Pengalaman kerja
          </Typography.Text>
          <IconSortAscDesc
            columnName={sortFieldJobApplicantsEnum.JOBEXPERIENCE}
            columnSortBy={columnsSortBy}
            columnSortField={columnsSortField}
          />
        </div>
      ),
      render: jobExperience => (
        <div
          data-testid="table_body_jobExperience"
          className="flex flex-col text-sm font-normal text-[#333539]"
        >
          <Typography.Text>{jobExperience} Tahun</Typography.Text>
        </div>
      ),
      onHeaderCell: () => {
        return {
          onClick: () => onSort(sortFieldJobApplicantsEnum.JOBEXPERIENCE),
        }
      },
    },
    {
      width: '15%',
      dataIndex: 'wage',
      key: 'wage',
      title: (
        <div className="flex">
          <Typography.Text className="text-sm font-semibold text-[#333539]">
            Harapan gaji
          </Typography.Text>
        </div>
      ),
      render: (wage: AdminJobBoardsVacanciesGetListApplicantsResponseItemsWage) => (
        <div
          data-testid="table_body_wage"
          className="flex flex-col text-sm font-normal text-[#333539]"
        >
          <Typography.Text>
            {rupiahFormat(wage.expected)} /{wage.unit}
          </Typography.Text>
        </div>
      ),
    },
    {
      width: '15%',
      dataIndex: 'status',
      key: 'status',
      title: (
        <Row>
          <Col span={20}>
            <Typography.Text className="text-sm font-semibold text-[#333539]">
              Status
            </Typography.Text>
          </Col>
          <Col span={4}>
            <MoreCircle size="16" color="#333539" className="self-center" />
          </Col>
        </Row>
      ),
      render: (status, item, index) => (
        <Row>
          <Col span={20}>
            <div className="w-fit">
              <Badge
                title={mapJobVacancyApplicantStatus(status)}
                variant={jobVacancyParticipantStatusBadgeVariant(status)}
              />
            </div>
          </Col>
          <Col span={4}>
            <Dropdown
              placement="bottomRight"
              trigger={['click']}
              dropdownRender={() => (
                <div className="flex flex-col gap-3 p-4 bg-white rounded-md shadow-xl">
                  <span className="font-semibold pt-2 px-3">Aksi</span>
                  {status !== JobBoardsVacanciesApplicationStatusEnum.Pending && (
                    <span
                      data-testid={`select_update_status_${JobBoardsVacanciesApplicationStatusEnum.Pending}`}
                      onClick={() =>
                        onOpenModalUpdateApplicantStatus(
                          item.id,
                          JobBoardsVacanciesApplicationStatusEnum.Pending,
                        )
                      }
                      className="hover:bg-primary py-1 px-3 bg-white cursor-pointer hover:text-white rounded-lg"
                    >
                      Menunggu
                    </span>
                  )}
                  {status !== JobBoardsVacanciesApplicationStatusEnum.Accepted && (
                    <span
                      data-testid={`select_update_status_${JobBoardsVacanciesApplicationStatusEnum.Accepted}`}
                      onClick={() =>
                        onOpenModalUpdateApplicantStatus(
                          item.id,
                          JobBoardsVacanciesApplicationStatusEnum.Accepted,
                        )
                      }
                      className="hover:bg-primary py-1 px-3 bg-white cursor-pointer hover:text-white rounded-lg"
                    >
                      Diterima
                    </span>
                  )}
                  {status !== JobBoardsVacanciesApplicationStatusEnum.Rejected && (
                    <span
                      data-testid={`select_update_status_${JobBoardsVacanciesApplicationStatusEnum.Rejected}`}
                      onClick={() =>
                        onOpenModalUpdateApplicantStatus(
                          item.id,
                          JobBoardsVacanciesApplicationStatusEnum.Rejected,
                        )
                      }
                      className="hover:bg-primary py-1 px-3 bg-white cursor-pointer hover:text-white rounded-lg"
                    >
                      Ditolak
                    </span>
                  )}
                </div>
              )}
              arrow
            >
              <Button data-testid={`button_action_${index}`} block className="bg-transparent">
                <Typography.Text className="font-medium cursor-pointer">
                  <IconCaretDown />
                </Typography.Text>
              </Button>
            </Dropdown>
          </Col>
        </Row>
      ),
    },
  ]

  return (
    <Table
      rowKey="id"
      loading={loading}
      data-testid="table_job_vacancy_applicant"
      className="table-primary table-primary--admin"
      columns={columns}
      dataSource={data}
      pagination={false}
      scroll={{ x: 1000 }}
    />
  )
}
